import { index, integer, jsonb, pgTable, text, timestamp, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

// =============================================================================
// BILLING — Launch-week §C. docs/briefs/launch-week-briefs.md §C, PRICING
// LOCKED (R154, 2026-07-02): $24.99/mo · $179/yr · $7.99/wk · $99 founder-year
// capped at first-100 · 14-day trial on every plan.
//
// TEST-MODE-READY, not live: no Stripe keys exist yet (W1-3 precedent —
// ADZUNA_APP_ID/ADZUNA_APP_KEY absent = the feature quietly no-ops, never a
// crash). STRIPE_SECRET_KEY/STRIPE_WEBHOOK_SECRET are optional Bindings
// (apps/api/lib/env.ts); billing routes serve {configured:false} honestly
// when absent, and the app's free tier keeps working regardless.
//
// Per-user (userId, text) — matches the codebase's prevailing text-keyed
// user_id convention (jobs/watchlist_sources/company_targets); the uuid
// migration tracked in CLAUDE.md is a later, separate pass.
//
// Two tables:
//   subscriptions   — one row per user's current Stripe subscription state.
//                      Upserted by the webhook handler on every relevant
//                      Stripe event; entitlements.ts reads this row (never
//                      re-derives from Stripe directly — this row IS the
//                      cache Stripe's own webhooks keep fresh).
//   billing_events  — append-only audit log (hard rule: audit log every
//                      meaningful action). Every webhook delivery is logged
//                      here BEFORE any side effect, keyed by Stripe's own
//                      event id, so a replayed webhook (Stripe retries on
//                      non-2xx, and can redeliver the same event id) is
//                      detected and skipped — the idempotency mechanism IS
//                      this table, not a separate dedup store.
// =============================================================================

/** The four paid plans (PRICING LOCKED, R154). 'founder' is $99/year, capped
 *  at the first 100 subscribers — enforced in billing.routes.ts by counting
 *  existing founder rows before creating a Checkout Session, never a soft
 *  cap the DB alone enforces (Postgres has no "first N rows" primitive). */
export type SubscriptionPlan = 'weekly' | 'monthly' | 'annual' | 'founder';

/** Stripe's own subscription status vocabulary, narrowed to the values this
 *  app's webhook handler actually writes. 'trialing' covers the 14-day trial
 *  every plan gets; 'past_due' is the "payment failed, grace period" state
 *  entitlements.ts reads to decide whether access should lapse. */
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled';

export const subscriptions = pgTable(
  'subscriptions',
  {
    id: id(),
    userId: text('user_id').notNull(),
    stripeCustomerId: varchar('stripe_customer_id', { length: 128 }),
    stripeSubscriptionId: varchar('stripe_subscription_id', { length: 128 }),
    plan: varchar('plan', { length: 16 }).$type<SubscriptionPlan>(),
    status: varchar('status', { length: 16 }).$type<SubscriptionStatus>(),
    currentPeriodEnd: timestamp('current_period_end', { withTimezone: true }),
    trialEnd: timestamp('trial_end', { withTimezone: true }),
    /** Set only for plan='founder' — the sequential slot number (1..100) this
     *  row occupies. Null for every other plan. The cap check in
     *  billing.routes.ts counts rows where founderSlot IS NOT NULL rather
     *  than trusting a counter column, so a canceled founder row still
     *  "holds" its slot (nothing-unrecoverable: a slot, once claimed, is
     *  never silently reissued to someone else — see entitlements.ts's
     *  founder-cap arithmetic tests for the exact rule). */
    founderSlot: integer('founder_slot'),
    /** PROVENANCE — which payment rail wrote this row (RevenueCat rail,
     *  docs/design/revenuecat-rail-2026-07-24.md). Additive + NULLABLE: every
     *  existing (Stripe-web) row reads null, and null is READ AS 'stripe_web'
     *  everywhere — no backfill, no behavior change. The Stripe webhook handler
     *  leaves this null (implicit stripe_web); the RC webhook stamps
     *  'revenuecat_store'. The entitlement resolver never reads this column
     *  (its SubscriptionRowLike input can't even see it) — provenance only. */
    rail: varchar('rail', { length: 24 }).$type<'stripe_web' | 'revenuecat_store'>(),
    /** PROVENANCE — the store-side stable subscription identity for an
     *  RC-fed row (RevenueCat's original_transaction_id), the RC analogue of
     *  stripe_subscription_id. Additive + NULLABLE (null on every Stripe row).
     *  The RC handler upserts on (userId, rail) and carries this for audit /
     *  traceability. */
    storeTransactionId: varchar('store_transaction_id', { length: 128 }),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('subscriptions_user_idx').on(t.userId),
    stripeCustomerIdx: index('subscriptions_stripe_customer_idx').on(t.stripeCustomerId),
    stripeSubscriptionIdx: index('subscriptions_stripe_subscription_idx').on(
      t.stripeSubscriptionId,
    ),
    planIdx: index('subscriptions_plan_idx').on(t.plan),
    storeTransactionIdx: index('subscriptions_store_transaction_idx').on(t.storeTransactionId),
  }),
);

/** Append-only. Every Stripe webhook delivery lands here first (eventId is
 *  the dedup key — a redelivered event id is detected via a lookup here
 *  BEFORE any subscription row is touched), successful or not. `payload`
 *  keeps the full event body for audit/replay-debugging (hard rule: audit
 *  log every meaningful action, never silence the trail). This table is
 *  never updated after insert and never deleted from (no deletions, ever). */
export const billingEvents = pgTable(
  'billing_events',
  {
    id: id(),
    /** Stripe's own event id (evt_...) — globally unique per Stripe, so this
     *  IS the idempotency key. A second webhook delivery for the same id is
     *  detected by a SELECT against this column and short-circuited to a 200
     *  without re-processing (Stripe's own retry/redelivery behavior treats
     *  any non-2xx as "try again later", so idempotent handling is required,
     *  not optional). */
    stripeEventId: varchar('stripe_event_id', { length: 128 }).notNull(),
    /** The Stripe event type verbatim (e.g. 'checkout.session.completed',
     *  'customer.subscription.updated'). Unknown/unhandled types are still
     *  logged here (with handled=false) — the brief's "unknown events logged
     *  + 200, never a 500 loop" — so the audit trail sees everything Stripe
     *  ever sent, not just what this app currently understands. */
    eventType: varchar('event_type', { length: 64 }).notNull(),
    userId: text('user_id'),
    /** True once this event's side effect (the subscription upsert) actually
     *  ran. False for a duplicate delivery that was skipped, or a type this
     *  handler doesn't act on. */
    handled: varchar('handled', { length: 8 }).default('false').notNull(),
    payload: jsonb('payload').$type<Record<string, unknown>>(),
    ...timestamps,
  },
  (t) => ({
    eventIdIdx: index('billing_events_event_id_idx').on(t.stripeEventId),
    userIdx: index('billing_events_user_idx').on(t.userId),
    typeIdx: index('billing_events_type_idx').on(t.eventType),
  }),
);
